<template>
  <div>
    <!-- Dialog to prompt users if an adjustment was applied -->
    <v-dialog v-model="adjustmentDialog" persistent max-width="680">
      <v-card class="pt-2">
        <v-card-title class="headline pl-5"> Loan Policy Updated </v-card-title>
        <v-card-text class="mt-0 pt-0">
          <p class="pt-0 pl-0 mb-3">
            The loan product policy terms have been adjusted due to
            <b>risk based adjustments</b>.
            {{
              disableActionButtons
                ? ''
                : ` ACCEPTING
            this policy will give you the fexibility to adjust the loan terms
            within the new policy.`
            }}
          </p>
          <v-row>
            <v-col>
              <v-card outlined class="pa-3 mt-0">
                <h3 class="text-subtitle-1 font-weight-bold">
                  Adjusted Product Policy Terms
                </h3>
                <ul class="pl-0 mt-2" style="list-style: none">
                  <li
                    class="mb-1"
                    v-for="(value, key) in adjustedTerms"
                    :key="key"
                  >
                    <span class="d-block">{{ value?.name }}</span>
                    <span v-if="!value.isSame" class="black--text">
                      {{ value?.prev.min }} - {{ value?.prev.max }} to
                      {{ value?.cur.min }} - {{ value?.cur.max }}
                    </span>
                    <span v-else class="black--text">
                      {{ value?.prev.min }} to {{ value?.cur.min }}
                    </span>
                  </li>
                </ul>
              </v-card>
            </v-col>

            <v-col>
              <v-card outlined class="pa-3 mt-0" style="height: 100%">
                <h3 class="text-subtitle-1 font-weight-bold">
                  Recommended Terms
                </h3>
                <div
                  class="d-flex justify-space-between align-center text-subtitle-2 mt-3 mb-2"
                >
                  <span class="font-weight-regular">Amount</span>
                  <span>{{ asMoney(recommendedAmount) }}</span>
                </div>
                <div
                  class="d-flex justify-space-between align-center text-subtitle-2 mt-3 mb-2"
                >
                  <span class="font-weight-regular">Tenure</span>
                  <span>{{ recommendedTenure }}</span>
                </div>
                <div
                  class="d-flex justify-space-between align-center text-subtitle-2 mt-3 mb-2"
                >
                  <span class="font-weight-regular">Interest (annual)</span>
                  <span>{{ asPercent(recommendedInterest) }}</span>
                </div>
                <div
                  class="d-flex justify-space-between align-center text-subtitle-2 mt-3 mb-2"
                >
                  <span class="font-weight-regular"
                    >{{ loan.frequencyName }} Payment</span
                  >
                  <span>{{ recommendedPayment }}</span>
                </div>
              </v-card>
            </v-col>
          </v-row>

          <div
            :class="`mt-3 py-3 ${
              disableActionButtons ? 'd-flex justify-end' : ''
            }`"
          >
            <v-btn
              v-if="disableActionButtons"
              text
              outlined
              :loading="continueLoading"
              :disabled="acceptLoading"
              class="shadow"
              @click="adjustmentDialog = false"
            >
              Close
            </v-btn>
            <v-btn
              v-if="!disableActionButtons"
              text
              block
              outlined
              :loading="continueLoading"
              :disabled="acceptLoading"
              class="shadow"
              @click="continueWithCurrentPolicy()"
            >
              Continue with current policy
            </v-btn>

            <v-btn
              v-if="!disableActionButtons"
              block
              class="mt-2 error"
              :loading="acceptLoading"
              :disabled="continueLoading"
              @click="acceptAdjustments()"
            >
              Accept Adjustments
            </v-btn>
          </div>
        </v-card-text>
        <!-- <v-divider></v-divider> -->
      </v-card>
    </v-dialog>
  </div>
</template>

<script>
import PageMixin from '@/mixins/page.mixin';
import { LoanPoolService } from '@/modules/loanpro/services/loan-pool.service';
import PaymentCalculationService from '@/modules/loanpro/services/payment-calculation.service';

export default {
  mixins: [PageMixin],

  props: {
    loan: {
      type: Object,
      default: () => ({})
    },
    disableActionButtons: {
      type: Boolean,
      default: false
    }
  },

  data() {
    return {
      useAdjustments: false,
      creditScoreLimit: 0,
      continueLoading: false,
      acceptLoading: false,
      adjustmentDialog: false
    };
  },

  watch: {},

  computed: {
    adjustedTerms() {
      const { adjustedTerms, frequencyNoun, frequencyPeriod, originalTerms } =
        this.loan;
      if (!adjustedTerms) return {};

      const {
        minInterest,
        maxInterest,
        loanCeiling,
        minTenure,
        maxTenure,
        shares,
        valueCollateralUsed
      } = originalTerms;

      const loanAmount = loanCeiling ?? 0;

      const minfrequencyTenure = (minTenure / 12) * frequencyPeriod;
      const maxfrequencyTenure = (maxTenure / 12) * frequencyPeriod;

      const adjustments = {
        amount: {
          name: 'Loan Amount',
          cur: {
            min: this.asMoney(
              this.defaultToZeroIfLower(+loanAmount + adjustedTerms?.loanAmount)
            ),
            max: this.asMoney(
              this.defaultToZeroIfLower(+loanAmount + adjustedTerms?.loanAmount)
            )
          },
          prev: {
            min: this.asMoney(this.defaultToZeroIfLower(loanAmount)),
            max: this.asMoney(this.defaultToZeroIfLower(loanAmount))
          },
          isSame: true
        },
        interest: {
          name: `Interest`,
          cur: {
            min: this.asPercent(+minInterest + adjustedTerms?.interest),
            max: this.asPercent(+maxInterest + adjustedTerms?.interest)
          },
          prev: {
            min: this.asPercent(minInterest),
            max: this.asPercent(maxInterest)
          }
        },
        shares: {
          name: 'Shares',
          cur: {
            min: this.asPercent(
              this.defaultToZeroIfLower(+shares + adjustedTerms?.shares)
            ),
            max: this.asPercent(
              this.defaultToZeroIfLower(+shares + adjustedTerms?.shares)
            )
          },
          prev: {
            min: this.asPercent(shares),
            max: this.asPercent(shares)
          },
          isSame: true
        },
        frequencyTenure: {
          name: `Tenure (${frequencyNoun})`,
          cur: {
            min: this.defaultToZeroIfLower(
              Math.ceil(+minfrequencyTenure + adjustedTerms?.frequencyTenure)
            ),
            max: this.defaultToZeroIfLower(
              Math.ceil(+maxfrequencyTenure + adjustedTerms?.frequencyTenure)
            )
          },
          prev: {
            min: Math.abs(minfrequencyTenure),
            max: Math.abs(maxfrequencyTenure)
          }
        },
        valueCollateralUsed: {
          name: 'Value Of Collateral Used',
          cur: {
            min: this.asPercent(
              this.defaultToZeroIfLower(
                +valueCollateralUsed + adjustedTerms?.valueCollateralUsed
              )
            ),
            max: this.asPercent(
              +this.defaultToZeroIfLower(
                valueCollateralUsed + adjustedTerms?.valueCollateralUsed
              )
            )
          },
          prev: {
            min: this.asPercent(valueCollateralUsed),
            max: this.asPercent(valueCollateralUsed)
          },
          isSame: true
        }
      };

      for (const k in adjustments) {
        const value = adjustments[k];
        if (value.prev.min === value.cur.min) delete adjustments[k];
      }

      return adjustments;
    },

    recommendedInterest: function () {
      const { adjustedTerms, interest } = this.loan ?? {};
      return +(+interest + adjustedTerms?.interest || 0).toFixed(2) || 0;
    },

    recommendedAmount: function () {
      const { adjustedTerms, loanAmount } = this.loan ?? {};
      return +(+loanAmount + adjustedTerms?.loanAmount).toFixed(2) || 0;
    },

    recommendedTenure: function () {
      const { adjustedTerms, frequencyTenure, frequencyNoun } = this.loan ?? {};

      return `${Math.ceil(
        +frequencyTenure + adjustedTerms?.frequencyTenure || 0
      )} ${frequencyNoun}(s)`;
    },

    recommendedPayment: function () {
      const {
        adjustedTerms,
        interest,
        loanAmount,
        frequencyPeriod,
        frequencyTenure
      } = this.loan ?? {};
      const amount = +loanAmount + +adjustedTerms?.loanAmount || 0;
      const intr = +interest + adjustedTerms?.interest || 0 || 0;
      const terms = +frequencyTenure + adjustedTerms?.frequencyTenure || 0;

      const term = PaymentCalculationService.getFrequencyTerm(
        frequencyPeriod,
        terms
      );

      const frequencyInterest = intr / 100 / frequencyPeriod;

      const { payment } = PaymentCalculationService.calculateRePaymentAmount(
        amount,
        term,
        frequencyInterest,
        this.loan?.loanProductDetails?.monthlyPaymentCalculation,
        this.loan?.loanProductDetails?.loanProductSubType
      );

      return `${this.asMoney(payment)}`;
    }
  },

  methods: {
    defaultToZeroIfLower(v) {
      return +v < 0 ? 0 : +v;
    },

    openDialog() {
      this.adjustmentDialog = true;
    },

    async continueWithCurrentPolicy() {
      try {
        this.continueLoading = true;
        const loanService = new LoanPoolService();
        const { data } = await loanService.saveCreditAssessmentStatus(
          this.loan.id,
          {
            adjustmentStatus: 'current-terms'
          }
        );
        this.$emit('updated', data);
        this.adjustmentDialog = false;
      } catch (e) {
        this.showSnack({
          color: 'error',
          title: `Failed to `,
          message: `Something went wrong trying to continue with current terms. Please try again.`
        });
      } finally {
        this.continueLoading = false;
      }
    },

    async acceptAdjustments() {
      try {
        this.acceptLoading = true;
        const loanService = new LoanPoolService();
        const { data } = await loanService.saveCreditAssessmentStatus(
          this.loan.id,
          {
            adjustmentStatus: 'adjustments-requested',
            adjustedTerms: this.loan.adjustedTerms
          }
        );

        this.$emit('accepted', data);
        this.adjustmentDialog = false;
      } catch (e) {
        this.showSnack({
          color: 'error',
          title: `Failed to `,
          message: `Something went wrong trying to accept adjustments. Please try again.`
        });
      } finally {
        this.acceptLoading = false;
      }
    }
  }
};
</script>
